import { FC, PointerEvent as ReactPointerEvent, useRef } from 'react';
import PropTypes from 'prop-types';
import { Box } from '@material-ui/core';
import { BBox } from '../utils/card-crop';

interface ManualCropOverlayProps {
  /** The crop box in the source image's NATIVE pixel coordinates. */
  box: BBox;
  /** Display pixels per native pixel (letterboxed display width / image natural width). */
  scale: number;
  /** The source image's native size, to clamp the box within. */
  bounds: { w: number; h: number };
  /** Clockwise preview rotation (deg) the overlay sits under, so drags map back to image space. */
  rotate?: number;
  /** Called with the updated box (native coords) as the operator drags (continuously). */
  onChange: (box: BBox) => void;
  /** Called once with the final box when a drag ends, for persisting the position. */
  onCommit?: (box: BBox) => void;
  /** The corner being dragged, in native coords (for a magnifier loupe), or null when idle. */
  onLoupe?: (point: { x: number; y: number } | null) => void;
}

// Corner order: 0 = top-left, 1 = top-right, 2 = bottom-right, 3 = bottom-left.
const CORNERS = [0, 1, 2, 3] as const;
type Corner = (typeof CORNERS)[number];

const MIN_NATIVE = 40; // don't let the box collapse below this (native px)
const HANDLE = 18; // corner handle size (display px)

type Drag =
  | { kind: 'move'; sx: number; sy: number; box: BBox }
  | { kind: 'corner'; corner: Corner; sx: number; sy: number; box: BBox };

/** The native-coord position of a given corner of a box. */
const cornerPoint = (b: BBox, corner: Corner) => ({
  x: corner === 0 || corner === 3 ? b.x : b.x + b.w,
  y: corner === 0 || corner === 1 ? b.y : b.y + b.h,
});

/**
 * A draggable axis-aligned crop rectangle over the (letterboxed) camera preview: drag a corner to
 * resize (the opposite corner stays put) or drag the body to move. Emits the box in native image
 * coordinates so the snap can be cropped to exactly what the operator framed. No rotation — a plain
 * rectangle.
 */
export const ManualCropOverlay: FC<ManualCropOverlayProps> = ({
  box,
  scale,
  bounds,
  rotate = 0,
  onChange,
  onCommit,
  onLoupe,
}) => {
  const drag = useRef<Drag | null>(null);

  const beginMove = (e: ReactPointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    (e.target as Element).setPointerCapture(e.pointerId);
    drag.current = { kind: 'move', sx: e.clientX, sy: e.clientY, box };
  };

  const beginCorner = (corner: Corner) => (e: ReactPointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    (e.target as Element).setPointerCapture(e.pointerId);
    drag.current = { kind: 'corner', corner, sx: e.clientX, sy: e.clientY, box };
    onLoupe?.(cornerPoint(box, corner));
  };

  const move = (e: ReactPointerEvent) => {
    const d = drag.current;
    if (!d) return;
    // The overlay rotates with the preview, so an on-screen drag delta must be inverse-rotated
    // back into the image's own (unrotated) frame before converting display px → native px.
    const sx = e.clientX - d.sx;
    const sy = e.clientY - d.sy;
    const rad = (rotate * Math.PI) / 180;
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);
    const dx = (cos * sx + sin * sy) / scale; // native px
    const dy = (-sin * sx + cos * sy) / scale;

    if (d.kind === 'move') {
      const x = Math.min(Math.max(0, d.box.x + dx), bounds.w - d.box.w);
      const y = Math.min(Math.max(0, d.box.y + dy), bounds.h - d.box.h);
      onChange({ x, y, w: d.box.w, h: d.box.h });
      return;
    }

    // Resize: the anchored (opposite) corner stays fixed; the dragged corner is clamped to
    // [anchor + MIN, image bound].
    const left = d.box.x;
    const top = d.box.y;
    const right = d.box.x + d.box.w;
    const bottom = d.box.y + d.box.h;
    const movesLeft = d.corner === 0 || d.corner === 3;
    const movesTop = d.corner === 0 || d.corner === 1;

    let nl = left;
    let nt = top;
    let nr = right;
    let nb = bottom;
    if (movesLeft) nl = Math.min(Math.max(0, left + dx), right - MIN_NATIVE);
    else nr = Math.max(Math.min(bounds.w, right + dx), left + MIN_NATIVE);
    if (movesTop) nt = Math.min(Math.max(0, top + dy), bottom - MIN_NATIVE);
    else nb = Math.max(Math.min(bounds.h, bottom + dy), top + MIN_NATIVE);

    const next = { x: nl, y: nt, w: nr - nl, h: nb - nt };
    onChange(next);
    onLoupe?.(cornerPoint(next, d.corner));
  };

  const end = () => {
    const wasDragging = drag.current !== null;
    drag.current = null;
    onLoupe?.(null);
    if (wasDragging) onCommit?.(box);
  };

  const bx = box.x * scale;
  const by = box.y * scale;
  const bw = box.w * scale;
  const bh = box.h * scale;
  const cornerPos: Record<Corner, { left: number; top: number; cursor: string }> = {
    0: { left: bx, top: by, cursor: 'nwse-resize' },
    1: { left: bx + bw, top: by, cursor: 'nesw-resize' },
    2: { left: bx + bw, top: by + bh, cursor: 'nwse-resize' },
    3: { left: bx, top: by + bh, cursor: 'nesw-resize' },
  };

  return (
    // Root doesn't capture pointer events (so it never blocks the rest of the viewer); only the
    // rectangle body and the corner handles are interactive.
    <Box sx={{ position: 'absolute', inset: 0, pointerEvents: 'none', touchAction: 'none' }}>
      <Box
        onPointerDown={beginMove}
        onPointerMove={move}
        onPointerUp={end}
        sx={{
          position: 'absolute',
          left: bx,
          top: by,
          width: bw,
          height: bh,
          border: '2px solid rgba(255,255,255,0.95)',
          // A thin dark outline keeps the white border visible on light photos too.
          boxShadow: '0 0 0 1px rgba(0,0,0,0.35), inset 0 0 0 1px rgba(0,0,0,0.2)',
          borderRadius: 1,
          cursor: 'move',
          pointerEvents: 'auto',
          touchAction: 'none',
        }}
      />
      {CORNERS.map((c) => (
        <Box
          key={c}
          onPointerDown={beginCorner(c)}
          onPointerMove={move}
          onPointerUp={end}
          sx={{
            position: 'absolute',
            left: cornerPos[c].left - HANDLE / 2,
            top: cornerPos[c].top - HANDLE / 2,
            width: HANDLE,
            height: HANDLE,
            borderRadius: '50%',
            backgroundColor: '#fff',
            boxShadow: '0 1px 4px rgba(0,0,0,0.5)',
            border: '1px solid rgba(0,0,0,0.12)',
            boxSizing: 'border-box',
            cursor: cornerPos[c].cursor,
            pointerEvents: 'auto',
            touchAction: 'none',
          }}
        />
      ))}
    </Box>
  );
};

ManualCropOverlay.propTypes = {
  // @ts-ignore
  box: PropTypes.object.isRequired,
  scale: PropTypes.number.isRequired,
  // @ts-ignore
  bounds: PropTypes.object.isRequired,
  rotate: PropTypes.number,
  onChange: PropTypes.func.isRequired,
  onCommit: PropTypes.func,
  onLoupe: PropTypes.func,
};
